<template>
	<div
		class="list--wrapper"
		:class="{
			'is-mobile': isMobileWebApp,
			'is-analysis': isAnalysisMode,
		}"
	>
		<!-- header -->
		<div class="header list--header">
			<div class="header-left">
				<slot
					name="title"
					:title="title"
					:subtitle="subtitle"
				>
					<h2>{{ title }}</h2>
					<p
						v-if="subtitle"
						class="list--subtitle"
					>
						{{ subtitle }}
					</p>
				</slot>
			</div>
			<div
				class="header-right"
				data-qa="list-filter"
			>
				<slot
					name="filters-dropdown"
					:filters-module="filtersModule"
				/>
			</div>
		</div>
		<div
			v-if="!isAnalysisMode"
			class="divider"
		/>
		<slot name="filter-badges" />
		<div
			v-if="!isMobileWebApp && filterBadges.length > 0"
			class="header-under"
		>
			<FilterBadges
				:badges="filterBadges"
				:clear-all-filters-label="labels.clearAllFilters"
				@remove-filter="handleRemoveFilter"
				@clear-filters="handleClearFilters"
			/>
		</div>
		<div
			v-if="!isAnalysisMode && !isMobileWebApp && filterBadges.length > 0"
			class="divider"
		/>
		<!-- list view -->
		<slot name="list-view" />
		<!-- footer -->
		<slot name="footer" />
	</div>
</template>

<script>
import FilterBadges from '../filter-badges';
import $I18n from '../../i18n';
import { mapGetters } from 'vuex';
export default {
	name:       'FilterableList',
	components: {
		FilterBadges,
	},
	props: {
		title: {
			type:    String,
			default: '',
		},
		subtitle: {
			type: String,
		},
		activeFilters: {
			type:    [Array, Object],
			default: null,
		},
		isFilter: {
			type:    Boolean,
			default: true,
		},
		labels: {
			type:    Object,
			default: () => ({
				cancelBtn:       $I18n.trans('commons.cancel'),
				clearAllFilters: $I18n.trans('analysis.clear_all_filters'),
			}),
		},
		dropdownDisabled: {
			type:    Boolean,
			default: false,
		},
		listContainerClass: {
			default: () => [],
		},
		listBodyClass: {
			default: () => [],
		},
		filterNoSelectionLabel: {
			type:    String,
			default: '',
		},
		filtersModule: {
			type:    String,
			default: null,
		},
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
			isAnalysisMode: 'app/isAnalysisMode',
		}),
		filterBadges() {
			if (!this.isFilter) {
				return [];
			}
			return this.activeFilters.map((f) => {
				return {
					id:    f.key,
					label: f.label,
				};
			});
		},
	},
	methods: {
		handleClearFilters() {
			this.handleActiveFiltersChanged([]);
		},
		handleRemoveFilter(id) {
			const activeFilters = this.activeFilters.filter((e) => e.key !== id);
			this.handleActiveFiltersChanged(activeFilters);
		},
		handleActiveFiltersChanged(filters) {
			const normalizedFilters = Array.isArray(filters) ? filters : [filters];
			this.$emit('filters-updated', normalizedFilters);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_layout';
@import '@videotools/sass/_fonts';

.header {
	display: flex;
	padding: 0 0 0 6px;
}

.is-analysis {
	.header-under {
		padding-left: 32px;
		padding-right: 32px;
	}
}

.header,
.header-under {
	border: 0;
	background: var(--color-grey-950);
	padding-left: 8px;
}

.header-right {
	display: flex;
	justify-content: flex-end;
	align-items: center;
}

.list--wrapper {
	.header-left {
		margin: 0 24px;
	}

	.header-right {
		margin: 0 24px;
	}

	&:not(.is-analysis) {
		.header,
			.header-under {
				background: none;
				padding-top: 10px;
			}

		.header-right {
			margin: 0;
		}

		.divider {
			height: 1px;
			margin-left: 8px;
			margin-right: 8px;
			background: var(--color-grey-900);
		}

		.header-left {
			height: 100%;
			display: flex;
			align-items: center;
			padding-top: 4px;
			padding-bottom: 4px;
			margin-left: 0;

			h2 {
				font-size: 18px;
				line-height: 24px;
				font-weight: 400;
			}
		}

		&.is-mobile {
			position: relative;

			.header.list--header {
				@include mobile-content-header;
			}

			.header-left {
				padding: 0;
				margin-left: 0;
			}

			.header-right {
				height: auto;
			}
		}
	}

	&.is-analysis {
		&.is-mobile {
			.header {
				min-height: 52px;

				h2 {
					@include text-md-2;
				}
			}

			.header-left {
				padding: 12px 0;
				margin: 0 16px 0 8px;
			}
		}
	}

	.list--header {
		z-index: initial;
		padding: 4px 8px;
	}

	.header-right {
		margin-left: 0;
		flex: 0 1 0;
	}

	.header-left {
		position: relative;
		flex: 1;
		overflow: hidden;
		margin-right: 12px;
		display: flex;
		align-items: center;
	}
}

.list--subtitle {
	@include text-sm-2;
	color: var(--color-grey-300);
}

</style>
